import { Controller } from "@hotwired/stimulus";

const TYPING_DELAY = 300;

type Params = Event & { params: Record<string, string> };
type Field = HTMLInputElement | HTMLSelectElement;

// Connects to data-controller="catalogue-filters", on the wrapper of a catalogue list: the filter form and the results
// frame beside it (docs/architecture/catalogue.md). The form is a plain GET form, so the page works without this. What
// the controller adds: a change submits at once and typing after a pause; the URL gets only the fields that are set (empty ones are left out of the request's form data);
// and everything in the results header (a chip's ×, Limpar tudo, a column header) acts through the form, so the form
// never shows something the results do not.
export default class extends Controller<HTMLElement> {
	static targets = ["form", "badge", "show", "order"];
	declare readonly formTarget: HTMLFormElement;
	declare readonly orderTarget: HTMLSelectElement;
	declare readonly badgeTarget: HTMLElement;
	declare readonly showTarget: HTMLElement;
	declare readonly hasOrderTarget: boolean;
	declare readonly hasBadgeTarget: boolean;
	declare readonly hasShowTarget: boolean;
	timer?: ReturnType<typeof setTimeout>;

	disconnect() {
		clearTimeout(this.timer);
	}

	// A chip, a concept or the sort select: the choice is made, so ask at once. Typing has its own pause, below.
	change(event: Event) {
		if (event.target instanceof HTMLInputElement && event.target.type === "search") return;
		this.submit();
	}

	input(event: Event) {
		if (!(event.target instanceof HTMLInputElement) || event.target.type !== "search") return;
		clearTimeout(this.timer);
		this.timer = setTimeout(() => this.submit(), TYPING_DELAY);
	}

	// When the form's data is read for the request (Turbo reads it for a GET's URL): a field left empty or at its
	// default is dropped from that copy, so the URL carries what the reader set and nothing else. The field itself is
	// untouched, so a search the reader is still typing in keeps its focus.
	prepare(event: FormDataEvent) {
		clearTimeout(this.timer);
		for (const field of this.fields()) if (this.isUnset(field)) event.formData.delete(field.name);
	}

	// A chip's ×: take that value out of the form, then submit.
	remove(event: Params) {
		event.preventDefault();
		const { field, value } = event.params;
		for (const input of this.fields(field)) {
			if (input instanceof HTMLInputElement && input.type === "checkbox") {
				if (input.value === value) input.checked = false;
			} else {
				this.reset(input, this.fields(field));
			}
		}
		this.submit();
	}

	// Limpar tudo and Limpar filtros: every search, chip and concept off. The order is not a filter and stays.
	clearAll(event: Event) {
		event.preventDefault();
		for (const field of this.fields()) {
			if (field instanceof HTMLSelectElement) continue;
			if (field.type === "checkbox") field.checked = false;
			else this.reset(field, this.fields(field.name));
		}
		this.submit();
	}

	// A column header: the form's order field takes the sort the link asks for.
	sort(event: Params) {
		event.preventDefault();
		if (!this.hasOrderTarget) return;
		this.orderTarget.value = event.params.order;
		this.submit();
	}

	// After the results frame loads: the form's filter count and its Mostrar button say what the results now say.
	sync() {
		const results = this.element.querySelector<HTMLElement>("[data-catalogue-results]");
		if (!results) return;
		const active = Number(results.dataset.catalogueActiveCount ?? 0);
		if (this.hasBadgeTarget) {
			this.badgeTarget.textContent = String(active);
			this.badgeTarget.classList.toggle("hidden", active === 0);
		}
		if (this.hasShowTarget) this.showTarget.textContent = results.dataset.catalogueShowLabel ?? "";
	}

	submit() {
		clearTimeout(this.timer);
		this.formTarget.requestSubmit();
	}

	fields(name?: string): Field[] {
		const all = [...this.formTarget.elements].filter(
			(element): element is Field => element instanceof HTMLInputElement || element instanceof HTMLSelectElement,
		);
		return name === undefined ? all : all.filter((field) => field.name === name);
	}

	// A text field is emptied; a radio group goes back to its blank choice (Todos).
	reset(field: Field, group: Field[]) {
		if (field instanceof HTMLInputElement && field.type === "radio") {
			const blank = group.find((other) => other.value === "");
			if (blank instanceof HTMLInputElement) blank.checked = true;
		} else {
			field.value = "";
		}
	}

	isUnset(field: Field) {
		if (field instanceof HTMLSelectElement) return field.value === (field.dataset.default ?? "");
		if (field.type === "radio") return field.checked && field.value === "";
		return field.type === "search" && field.value === "";
	}
}
